// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Header, Form, Button, Icon, Message } from 'semantic-ui-react';
import { brandingApi } from '../api/brandingApi';
import BrandingSectionEnhanced from '../components/BrandingSectionEnhanced';

function BrandingCreatePage() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [tenantId, setTenantId] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [brandingTier, setBrandingTier] = useState<'NONE' | 'GREY_LABEL' | 'WHITE_LABEL'>('NONE');
  const [logoUrl, setLogoUrl] = useState('');
  const [brandingConfig, setBrandingConfig] = useState<Record<string, unknown>>({});

  const handleChange = (field: string, value: unknown) => {
    if (field === 'brandingTier') {
      setBrandingTier(value as 'NONE' | 'GREY_LABEL' | 'WHITE_LABEL');
    } else if (field === 'logoUrl') {
      setLogoUrl(value as string);
    } else if (field === 'branding') {
      setBrandingConfig(value as Record<string, unknown>);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!tenantId.trim()) {
      setError('Tenant ID is required');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      await brandingApi.initBranding({
        tenantId: tenantId.trim(),
        subdomain: subdomain.trim() || undefined,
        displayName: displayName.trim() || undefined,
        brandingTier,
        logos: logoUrl ? { primary: logoUrl } : undefined,
        branding: Object.keys(brandingConfig).length > 0 ? brandingConfig : undefined,
      });
      setSuccess(true);
      setTimeout(() => navigate('/branding'), 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create branding');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container style={{ padding: '2rem' }}>
      <Button
        basic
        onClick={() => navigate('/branding')}
        style={{ marginBottom: '15px' }}
      >
        <Icon name='arrow left' />
        Back to Branding
      </Button>

      <Header as='h1'>
        <Icon name='paint brush' />
        <Header.Content>
          Create Branding
          <Header.Subheader>Initialize branding for a new tenant (e.g. provider)</Header.Subheader>
        </Header.Content>
      </Header>

      {error && (
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {success && (
        <Message positive>
          <Message.Header>Branding Created</Message.Header>
          <p>Branding initialized successfully. Redirecting...</p>
        </Message>
      )}

      <Form onSubmit={handleSubmit} loading={saving}>
        <Header as='h3' dividing style={{ marginTop: '20px' }}>
          Tenant Details
          <Header.Subheader>Identify the tenant this branding belongs to</Header.Subheader>
        </Header>

        <Form.Group widths='equal'>
          <Form.Field required>
            <label htmlFor='branding-tenant-id'>Tenant ID</label>
            <input
              id='branding-tenant-id'
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              placeholder='UUID of the brokerage or provider'
            />
          </Form.Field>
          <Form.Field>
            <label htmlFor='branding-subdomain'>Subdomain</label>
            <input
              id='branding-subdomain'
              value={subdomain}
              onChange={(e) => setSubdomain(e.target.value)}
              placeholder='e.g. acme-provider'
            />
          </Form.Field>
        </Form.Group>

        <Form.Field>
          <label htmlFor='branding-display-name'>Display Name</label>
          <input
            id='branding-display-name'
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder='e.g. Acme Insurance Provider'
          />
        </Form.Field>

        <BrandingSectionEnhanced
          brandingTier={brandingTier}
          logoUrl={logoUrl}
          branding={brandingConfig}
          onChange={handleChange}
        />

        <div style={{ marginTop: '20px' }}>
          <Button.Group>
            <Button type='button' onClick={() => navigate('/branding')}>
              Cancel
            </Button>
            <Button.Or />
            <Button positive type='submit' disabled={saving}>
              <Icon name='checkmark' />
              Create Branding
            </Button>
          </Button.Group>
        </div>
      </Form>
    </Container>
  );
}

export default BrandingCreatePage;
